import Box from '@mui/material/Box';
import { FormattedMessage, useIntl } from 'react-intl';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { ReactNode } from 'react';
import {
  Feature,
  getAddOnCodeByFeature,
  getFeatureByAddOn,
  getFeatureFromPlan,
  isTrueBundleFeature,
  Order,
  OrderItem,
  PlanInterval,
  RecurlyAddOn,
} from '../../../../models';
import { CurrencyDisplay, formatCurrency } from '../../../../components';
import { getSummaryQuantityLabel } from '../../../plan/component/true-platform/summaryUnits';
import {
  formatTrueBundleComponentName,
  getFallbackFreeCrossBrowserSessions,
  sortFreeCrossBrowserComponentsLast,
} from '../../../plan/component/true-platform/trueBundleComponents';
import { DEFAULT_CURRENCY_FORMAT_OPTIONS, USD_CURRENCY } from '../../../../utils/Constants';

// Grid template shared by the header row and every item row (per design).
const GRID_TEMPLATE_COLUMNS = 'minmax(220px, 2.4fr) 152px 92px 130px 150px';

const useStyles = makeStyles(({
  scrollContainer: {
    overflowX: 'auto',
  },
  table: {
    minWidth: 720,
  },
  headerRow: {
    display: 'grid',
    gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
    gap: 20,
    alignItems: 'center',
    padding: '13px 30px',
    backgroundColor: '#F7F9F8',
    borderTop: '1px solid #EEF2F0',
  },
  headerCell: {
    fontSize: 11.5,
    fontWeight: 700,
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    color: '#5B6661',
  },
  itemRow: {
    display: 'grid',
    gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
    gap: 20,
    alignItems: 'center',
    padding: '18px 30px',
    borderTop: '1px solid #EEF2F0',
  },
  addonRow: {
    display: 'grid',
    gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
    gap: 20,
    alignItems: 'center',
    padding: '0 30px 14px',
  },
  productName: {
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 15.5,
    fontWeight: 600,
    letterSpacing: '-.01em',
    color: '#16141D',
  },
  addonName: {
    borderLeft: '2px solid #E1E7E4',
    fontSize: 13,
    fontWeight: 400,
    lineHeight: '250%',
    color: '#5B6661',
    marginLeft: 16,
    paddingLeft: 16,
  },
  intervalPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    padding: '5px 12px 5px 9px',
    borderRadius: 999,
    backgroundColor: '#EDF8F3',
    color: '#0F8461',
    fontSize: 12.5,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  intervalIcon: {
    display: 'inline-flex',
    fontSize: 13,
  },
  quantityCell: {
    textAlign: 'center',
    fontSize: 14,
    color: '#3C3A50',
    fontVariantNumeric: 'tabular-nums',
  },
  unitPriceCell: {
    textAlign: 'right',
    fontSize: 14,
    color: '#16141D',
    fontVariantNumeric: 'tabular-nums',
  },
  extendedPriceCell: {
    textAlign: 'right',
    fontSize: 14,
    fontWeight: 600,
    color: '#16141D',
    fontVariantNumeric: 'tabular-nums',
  },
  addonCell: {
    fontSize: 13,
    color: '#5B6661',
  },
  totalsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '22px 30px',
    borderTop: '1px solid #EEF2F0',
  },
  totalsColumn: {
    width: 'min(360px, 100%)',
    display: 'flex',
    flexDirection: 'column',
    gap: 11,
  },
  totalsRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 24,
  },
  totalsLabel: {
    fontSize: 13.5,
    color: '#5F5D75',
  },
  totalsValue: {
    fontSize: 14,
    fontWeight: 600,
    color: '#16141D',
    fontVariantNumeric: 'tabular-nums',
  },
  totalsDivider: {
    height: 1,
    backgroundColor: '#EEF2F0',
    margin: '4px 0 2px',
  },
  orderTotalLabel: {
    fontSize: 15,
    fontWeight: 700,
    color: '#16141D',
  },
  orderTotalValue: {
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 20,
    fontWeight: 700,
    letterSpacing: '-.01em',
    color: '#0F8461',
    fontVariantNumeric: 'tabular-nums',
  },
}));

export const OrderDetails = ({ order }: { order: Order }) => {
  const classes = useStyles();
  const intl = useIntl();
  const discountAmount = order.orderItems
    .reduce((sum, { discountAmount }) => sum + discountAmount, 0);

  const getProductName = (item: OrderItem) => {
    let productName;

    const feature = getFeatureFromPlan(item.planId);
    // A True Bundle is named per channel by the BE ("True Manual Starter" online vs "True
    // Manual" direct) and both channels share one feature, so the stored name wins.
    if (isTrueBundleFeature(feature)) {
      return item.productName;
    }
    if (feature) {
      productName = intl.formatMessage({ id: `feature.${feature.toLowerCase()}.product.name` });
    }

    // for purchase order items with type are stater package,
    // show the package name in the product name
    if (item.type === 'STARTER_PACKAGE' && order.origin === 'PURCHASE') {
      const prefix = `${intl.formatMessage({ id: `promotion.sp.${item.productName.toLowerCase()}.name` })}`;
      return `${prefix} - ${productName}`;
    }
    return productName ?? item.productName;
  };

  const isLegacyTruePlatformOrderItem = (item: OrderItem) => {
    const feature = getFeatureFromPlan(item.planId);
    return feature === Feature.TRUE_TEAM;
  };

  const getAddonDisplayName = (item: OrderItem) => {
    const feature = getFeatureFromPlan(item.planId);
    const addOnCode = feature ? getAddOnCodeByFeature(feature) : undefined;
    return addOnCode
      ? intl.formatMessage({ id: `addon.${addOnCode}.display.name` })
      : getProductName(item);
  };

  const renderIntervalPill = (interval: PlanInterval) => (
    <span>
      <span className={classes.intervalPill}>
        <span className={classes.intervalIcon}>
          <CalendarTodayOutlinedIcon fontSize="inherit" />
        </span>
        <FormattedMessage id={`page.subscription.billing.interval.${interval.toLowerCase()}.short`} />
      </span>
    </span>
  );

  const renderHeaderRow = () => (
    <div
      key="order-details-table-header"
      id="order-details-table-header"
      className={classes.headerRow}
    >
      <span className={classes.headerCell}>
        <FormattedMessage id="page.order.history.order.item.product.title" />
      </span>
      <span className={classes.headerCell}>
        <FormattedMessage id="page.order.history.order.item.billing.cycle.title" />
      </span>
      <span className={classes.headerCell} style={{ textAlign: 'center' }}>
        <FormattedMessage id="page.order.history.order.item.quantity.title" />
      </span>
      <span className={classes.headerCell} style={{ textAlign: 'right' }}>
        <FormattedMessage id="page.order.history.order.item.unit.price.title" />
      </span>
      <span className={classes.headerCell} style={{ textAlign: 'right' }}>
        <FormattedMessage id="page.order.history.order.item.extended.price.title" />
      </span>
    </div>
  );

  const renderNormalItemRow = (item: OrderItem) => (
    <div
      key={`order-details-table-item-${item.id}`}
      id={`order-details-table-item-${item.id}`}
      className={classes.itemRow}
    >
      <span className={classes.productName}>{getProductName(item)}</span>
      {renderIntervalPill(item.billingCycle)}
      <span className={classes.quantityCell}>{item.quantity}</span>
      <span className={classes.unitPriceCell}>
        <CurrencyDisplay amount={item.unitAmount} currency={USD_CURRENCY} />
      </span>
      <span className={classes.extendedPriceCell}>
        <CurrencyDisplay amount={item.totalAmount} currency={USD_CURRENCY} />
      </span>
    </div>
  );

  const renderAddonItemRow = (item: OrderItem) => (
    <div
      key={`order-details-table-item-${item.id}`}
      id={`order-details-table-item-${item.id}`}
      className={classes.addonRow}
    >
      <span className={classes.addonName}>{getAddonDisplayName(item)}</span>
      {/* Empty billing interval column – addon items don't show a billing interval */}
      <span />
      <span className={`${classes.quantityCell} ${classes.addonCell}`}>{item.quantity}</span>
      <span className={`${classes.unitPriceCell} ${classes.addonCell}`}>
        <CurrencyDisplay amount={item.unitAmount} currency={USD_CURRENCY} />
      </span>
      <span className={`${classes.extendedPriceCell} ${classes.addonCell}`}>
        <CurrencyDisplay amount={item.totalAmount} currency={USD_CURRENCY} />
      </span>
    </div>
  );

  // A True Bundle component: the package name + quantity it was sold with, never a price - the
  // whole package price sits on the parent row (Decision 2). The FREE TestCloud Cross Browser
  // grant renders exactly the same way (Decision 7 revised 2026-09-29: no "included" label).
  const renderComponentRow = (
    rowId: string,
    name: string,
    quantityLabel: string,
  ) => (
    <div key={rowId} id={rowId} className={classes.itemRow}>
      <span className={classes.addonName}>{name}</span>
      <span />
      <span className={`${classes.quantityCell} ${classes.addonCell}`}>{quantityLabel}</span>
      {/* No unit price and no extended price: a bundle is priced as one package. */}
      <span />
      <span />
    </div>
  );

  // `planId` is the Recurly add-on code.
  const renderBundleComponentRow = (item: OrderItem) => renderComponentRow(
    `order-details-table-item-${item.id}`,
    formatTrueBundleComponentName(intl, item.planId ?? '', item.productName),
    getSummaryQuantityLabel(getFeatureByAddOn(item.planId ?? ''), item.quantity),
  );

  // Order placed before the BE stored the free grant as a component row (Decision 4): the line
  // is drawn from the seat components the order does store.
  const renderFallbackFreeCrossBrowserRow = (parent: OrderItem, quantity: number) => (
    renderComponentRow(
      `order-details-table-item-${parent.id}-free-cross-browser`,
      formatTrueBundleComponentName(intl, RecurlyAddOn.TESTCLOUD_SESSION_CROSSBROWSER_FREE),
      getSummaryQuantityLabel(
        getFeatureByAddOn(RecurlyAddOn.TESTCLOUD_SESSION_CROSSBROWSER_FREE),
        quantity,
      ),
    )
  );

  const renderOrderItemRows = () => {
    // A bundle order carries explicit BUNDLE_COMPONENT rows pointing at their priced parent, so
    // it never needs the legacy "everything else is an add-on" heuristic (which mis-groups a
    // mixed order - see OrderServiceImpl.convertOrderItems).
    const componentItems = order.orderItems.filter(item => item.type === 'BUNDLE_COMPONENT');
    if (componentItems.length > 0) {
      const parentItems = order.orderItems.filter(item => item.type !== 'BUNDLE_COMPONENT');
      const parentIds = new Set(parentItems.map(item => item.id));
      return [
        ...parentItems.flatMap(parent => {
          const components = componentItems
            .filter(component => component.parentOrderItemId === parent.id);
          const fallbackFreeCrossBrowser = getFallbackFreeCrossBrowserSessions(components);
          // The free Cross Browser line always closes the group (Decision 11).
          return [
            renderNormalItemRow(parent),
            ...sortFreeCrossBrowserComponentsLast(components).map(renderBundleComponentRow),
            ...(fallbackFreeCrossBrowser > 0
              ? [renderFallbackFreeCrossBrowserRow(parent, fallbackFreeCrossBrowser)]
              : []),
          ];
        }),
        // Orphans (parent archived / not returned) still have to be visible.
        ...sortFreeCrossBrowserComponentsLast(componentItems
          .filter(component => !parentIds.has(component.parentOrderItemId ?? -1)))
          .map(renderBundleComponentRow),
      ];
    }

    const truePlatformItem = order.orderItems.find(isLegacyTruePlatformOrderItem);

    if (truePlatformItem) {
      // Legacy True Platform order: the bundle row followed by indented add-on rows.
      const addonItems = order.orderItems.filter(item => item.id !== truePlatformItem.id);
      return [
        renderNormalItemRow(truePlatformItem),
        ...addonItems.map(renderAddonItemRow),
      ];
    }

    return order.orderItems.map(renderNormalItemRow);
  };

  const renderTotalsRow = (id: string, value: ReactNode) => (
    <div
      key={`order-details-table-summarize-${id}`}
      id={`order-details-table-summarize-${id}`}
      className={classes.totalsRow}
    >
      <Typography component="span" className={classes.totalsLabel}>
        <FormattedMessage id={id} />
      </Typography>
      <Typography component="span" className={classes.totalsValue}>
        {value}
      </Typography>
    </div>
  );

  const renderTotals = () => (
    <div id="order-details-table-summarize" className={classes.totalsContainer}>
      <div className={classes.totalsColumn}>
        {discountAmount > 0 && renderTotalsRow(
          'page.order.history.order.item.discount.title',
          (
            <FormattedMessage
              id="page.order.history.order.item.discount.value"
              values={{
                discountAmount: formatCurrency(
                  intl,
                  discountAmount,
                  DEFAULT_CURRENCY_FORMAT_OPTIONS,
                ),
              }}
            />
          ),
        )}
        {renderTotalsRow(
          'page.order.history.order.item.grand.total.title',
          (
            <FormattedMessage
              id="page.order.history.order.item.grand.total.value"
              values={{
                amount: formatCurrency(
                  intl,
                  (order.totalAmount - order.taxAmount),
                  DEFAULT_CURRENCY_FORMAT_OPTIONS,
                ),
              }}
            />
          ),
        )}
        {renderTotalsRow(
          'page.order.history.order.item.tax.title',
          (
            <FormattedMessage
              id="page.order.history.order.item.tax.value"
              values={{
                amount: formatCurrency(intl, order.taxAmount, DEFAULT_CURRENCY_FORMAT_OPTIONS),
              }}
            />
          ),
        )}
        <div className={classes.totalsDivider} />
        <div className={classes.totalsRow}>
          <Typography component="span" className={classes.orderTotalLabel}>
            <FormattedMessage id="page.order.history.order.item.total.title" />
          </Typography>
          <Typography component="span" className={classes.orderTotalValue}>
            <FormattedMessage
              id="page.order.history.order.item.total.value"
              values={{
                amount: formatCurrency(intl, order.totalAmount, DEFAULT_CURRENCY_FORMAT_OPTIONS),
              }}
            />
          </Typography>
        </div>
      </div>
    </div>
  );

  return (
    <Box id="order-details-container" display="flex" flexDirection="column">
      <div className={classes.scrollContainer}>
        <div className={classes.table}>
          {/* Order header row */}
          {renderHeaderRow()}
          {/* Order item rows */}
          {renderOrderItemRows()}
        </div>
      </div>
      {/* Order totals */}
      {renderTotals()}
    </Box>
  );
};
